import { TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Skeleton } from '@sorare/core/src/atoms/loader/Skeleton';
import Dialog from '@sorare/core/src/components/dialog';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';

import {
  RewardCardDialog,
  Props as RewardCardDialogProps,
} from '@sorare/gameplay/src/components/rewards/RewardCardDialog';

import cardBg from 'components/onboarding/S3Onboarding/assets/cardBg.jpg';

import { FirstWeekClaim } from './FirstWeekClaim';
import {
  NBADAilyClaimDialogQuery,
  NBADAilyClaimDialogQueryVariables,
} from './__generated__/index.graphql';

const Root = styled(Vertical)`
  padding: var(--triple-unit) 0;
  flex: 1;
  height: 100%;
  min-height: min(calc(100vh - var(--unit) * 8), 800px);

  /* First child is the dialog close button */
  & > *:not(:last-child):not(:first-child) {
    border-bottom: 1px solid var(--c-nd-200);
    padding-bottom: var(--triple-unit);
  }

  background-image: url(${cardBg});
  background-size: cover;
  background-position: top;
  background-repeat: no-repeat;
`;

const CLAIM_TICKET_QUERY = gql`
  query NBADAilyClaimDialogQuery {
    currentUser {
      slug
      nbaUserProfile {
        id
        dailyClaimTasks {
          id
          ...FirstWeekClaim_nbaManagerTask
        }
      }
    }
  }
  ${FirstWeekClaim.fragments.nbaManagerTask}
` as TypedDocumentNode<
  NBADAilyClaimDialogQuery,
  NBADAilyClaimDialogQueryVariables
>;

type ContentProps = {
  data?: NBADAilyClaimDialogQuery;
  onClose: () => void;
  onClaimCard: ({
    card,
    ftue,
  }: {
    card: RewardCardDialogProps['card'];
    ftue: boolean;
  }) => void;
};

const Content = ({ data, onClose, onClaimCard }: ContentProps) => {
  const currentUser = data?.currentUser;

  if (!currentUser?.nbaUserProfile) {
    return null;
  }

  return (
    <>
      {currentUser.nbaUserProfile.dailyClaimTasks.length > 0 && (
        <FirstWeekClaim
          dailyClaimTasks={currentUser.nbaUserProfile.dailyClaimTasks}
          onClose={onClose}
          onClaimCard={({ card, ftue }) => onClaimCard({ card, ftue })}
        />
      )}
    </>
  );
};

type Props = {
  open: boolean;
  onClose: () => void;
};

export const DailyClaimDialog = ({ open, onClose }: Props) => {
  const { data, loading } = useQuery(CLAIM_TICKET_QUERY, { skip: !open });
  const currentUser = data?.currentUser;

  const [rewardCardDialogProps, setRewardCardDialogProps] = useState<{
    ftue: boolean;
    card: RewardCardDialogProps['card'];
  } | null>(null);

  const hasClaimTasks =
    (currentUser?.nbaUserProfile?.dailyClaimTasks || []).length > 0;

  return (
    <>
      {hasClaimTasks && (
        <Dialog
          open={open}
          maxWidth="xs"
          fullWidth
          hideHeader
          onClose={onClose}
        >
          <Skeleton loading={loading}>
            <Root gap={3}>
              <Content
                data={data}
                onClose={onClose}
                onClaimCard={({ card, ftue }) =>
                  setRewardCardDialogProps({ card, ftue })
                }
              />
            </Root>
          </Skeleton>
        </Dialog>
      )}
      <RewardCardDialog
        ftue={rewardCardDialogProps?.ftue || false}
        card={rewardCardDialogProps?.card}
        open={!!rewardCardDialogProps}
        onClose={() => setRewardCardDialogProps(null)}
      />
    </>
  );
};
